import { Injectable } from '@angular/core';
import * as signalR from '@aspnet/signalr';
import { Observable, Subject } from 'rxjs';
import { environment } from '@env/environment';
import { chunkItems } from '@shared/functions/chunkArray';

export interface LiveValue {
  identifier: string;
  value: any;
  timestamp: Date;
  lastValueChange: Date;
}

let identifier = 0;

@Injectable({
  providedIn: 'root'
})
export class LiveValueService {
  private readonly connectionStorage: { [key: string]: signalR.HubConnection };

  constructor() {
    this.connectionStorage = {};
  }

  public buildConnection(callback: Subject<any>, route?: string): string {
    const path = route ? route : '/hub';

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(this.getUrl() + path, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets
      })
      .build();

    const id = `LiveValue_${identifier++}`;
    this.connectionStorage[id] = connection;

    this.handleDisconnect(id, callback);

    connection.on('Send', (data) => callback.next(data));

    return id;
  }

  public startConnection(id: string): Observable<void> {
    const result = new Subject<void>();
    const connection = this.connectionStorage[id];
    if (connection) {
      connection
        .start()
        .catch((e) => {
          // console.log(e)
        })
        .then(() => {
          result.observers.forEach((ob) => {
            ob.next(undefined);
            ob.complete();
          });
          result.complete();
        });
    }
    return result.asObservable();
  }

  public subscribeManyForSignals(id: string, signalIds: string[]): void {
    const chunks = chunkItems(signalIds);
    for(const chunk of chunks) {
      this.subscribeMany(id, chunk);
    }

  }

  public subscribeManyForGroups(id: string, groupIds: string[]): void {
    const chunks = chunkItems(groupIds);
    for(const chunk of chunks) {
      this.subscribeMany(
        id,
        chunk.map((sId) => `${sId}`)
      );
    }

  }

  private subscribeMany(id: string, args: any[]) {
    const connection = this.connectionStorage[id];
    if (connection) {
      connection.invoke('SubscribeMany', args).catch((e) => {
        console.groupCollapsed('live-value.service.ts');
        console.log(e);
        console.groupEnd();
      });
    }
  }

  public setOnPush(id: string, onPush: boolean): void {
    const connection = this.connectionStorage[id];
    if (connection) {
      connection.invoke('ChangeModeAsync', onPush).catch((e) => {
        // console.log(e);
      });
    }
  }

  public setInvterval(id: string, interval: number): void {
    const connection = this.connectionStorage[id];
    if (connection) {
      connection.invoke('ChangeIntervalAsync', interval).catch((e) => {
        // console.log(e);
      });
    }
  }

  private handleDisconnect(id: string, subject: Subject<void>) {
    subject.subscribe(
      () => {},
      (e) => {console.log(e)},
      () => {
        if (this.connectionStorage[id].state === 1) {
          this.connectionStorage[id]
            .stop()
            .catch((e) => console.log(e))
            .then(() => console.log('HubClosed'));
          delete this.connectionStorage[id];
        }
      }
    );
  }


  private getUrl(): string {
    return environment.services.live;
  }
}
